import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
} from '@mui/material';
import { GitBranch, Plus } from 'lucide-react';
import { ContentTreeNode } from './ContentTreeNode';
import type { ContentBlock } from './interface';
import { BlockModal } from './BlockModal';
import { BlockCreateModal } from './BlockCreateModal';

const API_URL = import.meta.env.VITE_API_URL;

export function ContentTree() {
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [blockAddOpen, setBlockAddOpen] = useState(false);

  useEffect(() => {
    async function fetchBlocks() {
      try {
        const res = await axios.get<{ data: ContentBlock[] }>(
          `${API_URL}/content`,
        );
        setBlocks(res.data.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchBlocks();
  }, []);

  const handleSelectBlock = (block: ContentBlock) => {
    setSelectedBlockId(block.blockId);
    setModalOpen(true);
  };

  const handleToggleExpanded = (blockId: number) => {
    setExpandedBlocks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(blockId)) newSet.delete(blockId);
      else newSet.add(blockId);
      return newSet;
    });
  };

  const handleDeleteBlock = async (blockId: number) => {
    try {
      await axios.delete(`${API_URL}/content/${blockId}`);

      const res = await axios.get<{ data: ContentBlock[] }>(
        `${API_URL}/content`,
      );
      setBlocks(res.data.data);

      setExpandedBlocks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(blockId);
        return newSet;
      });

      if (selectedBlockId === blockId) {
        setSelectedBlockId(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const rootBlocks = blocks.filter((b) => b.parentId == null);

  return (
    <Box sx={{ margin: 1 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 4, overflow: 'hidden' }}>
        <CardHeader
          sx={{
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            color: 'white',
            py: 3,
          }}
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                }}
              >
                <GitBranch size={20} />
              </Box>
              <Box>
                <Typography variant="h6">Структура контенту</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Управління блоками і кнопками
                </Typography>
              </Box>
            </Stack>
          }
          action={
            <Chip
              label={`Кількість блоків: ${blocks.length}`}
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          }
        />

        <CardContent sx={{ p: 4 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => setBlockAddOpen(true)}
            >
              Додати блок
            </Button>
          </Stack>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rootBlocks.map((block) => (
              <ContentTreeNode
                key={block.blockId}
                block={block}
                allBlocks={blocks}
                level={0}
                isSelected={selectedBlockId === block.blockId}
                isExpanded={expandedBlocks.has(block.blockId)}
                expandedBlocks={expandedBlocks}
                selectedBlockId={selectedBlockId ?? undefined}
                onSelectBlock={handleSelectBlock}
                onToggleExpanded={handleToggleExpanded}
                onDeleteBlock={handleDeleteBlock}
              />
            ))}

            {blocks.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8, opacity: 0.6 }}>
                <GitBranch size={48} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Немає блоків контенту
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Створіть перший блок для початку роботи
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <BlockModal
        blockId={selectedBlockId}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpdateTree={async () => {
          const res = await axios.get(`${API_URL}/content`);
          setBlocks(res.data.data);
        }}
      />

      <BlockCreateModal
        open={blockAddOpen}
        onClose={() => setBlockAddOpen(false)}
        onCreated={async () => {
          setBlockAddOpen(false);
          try {
            const res = await axios.get<{ data: ContentBlock[] }>(
              `${API_URL}/content`,
            );
            setBlocks(res.data.data);
          } catch (err) {
            console.error(err);
          }
        }}
      />
    </Box>
  );
}
